import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { Client } from "@/features/clients/types";
import type {
  Form2848Representative,
  FormSubmission,
  GenerateFor,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikTextarea } from "./formik-controls";
import { PoaRepresentativesTable } from "./poa-representatives-table";

// --- Form WV-2848 editable payload -------------------------------------------
// snake_case keys mirror `apps/forms/mappings/form_wv2848.py` `build_defaults`
// (passed verbatim, no snake→camel). Taxpayer/rep identity is filled live from
// the Client/Practitioner records server-side, not typed here.

/** WV expiration group — mutually exclusive; each drives one PDF checkbox. */
export type WvExpiration = "revoked" | "date" | "satisfied" | "other";
/** WV acts-authorized radio — full authority vs. restricted (with narrative). */
export type WvActs = "FULL" | "REST";

/** WV matter row: type of tax + year(s)/period(s) (no per-row form field). */
export interface FormWv2848Matter {
  tax_type: string;
  tax_years: string;
}

export interface FormWv2848Data {
  generate_for: GenerateFor;
  representatives: Form2848Representative[];
  matters: FormWv2848Matter[];
  expiration: WvExpiration;
  exp_date: string;
  exp_other: string;
  acts_authorized: WvActs;
  restrictions: string;
}

/** The WV-2848 template has five tax-matter rows. */
export const MAX_WV_MATTERS = 5;

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const EXPIRATION_OPTIONS: { value: WvExpiration; label: string }[] = [
  { value: "revoked", label: "Until I revoke it in writing" },
  { value: "date", label: "On a specific date" },
  { value: "satisfied", label: "When the tax matter is satisfied" },
  { value: "other", label: "Other" },
];

const EMPTY_MATTER: FormWv2848Matter = { tax_type: "", tax_years: "" };

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  matters: Yup.array()
    .of(
      Yup.object({
        tax_type: Yup.string().max(60, "Too long"),
        tax_years: yearRange,
      }),
    )
    .max(MAX_WV_MATTERS),
  exp_other: Yup.string().max(30, "Max 30 characters"),
  acts_authorized: Yup.string().oneOf(["FULL", "REST"]).required(),
  restrictions: Yup.string().max(400, "Too long"),
});

function toInitialValues(data: Record<string, unknown>): FormWv2848Data {
  const d = data as Partial<FormWv2848Data>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    matters: d.matters && d.matters.length > 0 ? d.matters : [{ ...EMPTY_MATTER }],
    expiration: d.expiration ?? "revoked",
    exp_date: d.exp_date ?? "",
    exp_other: d.exp_other ?? "",
    acts_authorized: d.acts_authorized ?? "FULL",
    restrictions: d.restrictions ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/** WV tax-matters table — five rows of (type of tax, year(s)/period(s)). */
function WvMattersTable() {
  const { values } = useFormikContext<FormWv2848Data>();
  const matters = values.matters;
  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type of Tax</TableHead>
                  <TableHead className="w-56">Year(s) or Period(s)</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_type`}
                        placeholder="e.g. Personal Income Tax"
                        aria-label={`Type of tax for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Years for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_WV_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_WV_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form WV-2848 fits up to {MAX_WV_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

/** WV expiration group — a radio choice, with a conditional date / other input. */
function ExpirationField() {
  const [field, , helpers] = useField<WvExpiration>("expiration");
  return (
    <div className="flex flex-col gap-3">
      <RadioGroup
        className="flex flex-col gap-2"
        aria-label="Expiration"
        value={field.value}
        onValueChange={(v) => helpers.setValue(v as WvExpiration)}
      >
        {EXPIRATION_OPTIONS.map((o) => (
          <Label
            key={o.value}
            htmlFor={`exp-${o.value}`}
            className="flex items-center gap-2 font-normal"
          >
            <RadioGroupItem id={`exp-${o.value}`} value={o.value} />
            {o.label}
          </Label>
        ))}
      </RadioGroup>
      {field.value === "date" && (
        <div className="grid max-w-xs gap-2">
          <Label htmlFor="exp_date">Expiration date</Label>
          <ExpDateField />
        </div>
      )}
      {field.value === "other" && (
        <div className="grid max-w-md gap-2">
          <Label htmlFor="exp_other">Other (max 30 characters)</Label>
          <ExpOtherField />
        </div>
      )}
    </div>
  );
}

function ExpDateField() {
  const [field] = useField("exp_date");
  return <Input id="exp_date" placeholder="MM/DD/YYYY" {...field} />;
}

function ExpOtherField() {
  const [field] = useField("exp_other");
  return <Input id="exp_other" maxLength={30} {...field} />;
}

/** WV acts-authorized radio (full authority vs. restricted) + narrative. */
function ActsAuthorizedField() {
  const [field, , helpers] = useField<WvActs>("acts_authorized");
  return (
    <div className="flex flex-col gap-3">
      <RadioGroup
        className="flex flex-col gap-2"
        aria-label="Acts authorized"
        value={field.value}
        onValueChange={(v) => helpers.setValue(v as WvActs)}
      >
        <Label htmlFor="acts-FULL" className="flex items-center gap-2 font-normal">
          <RadioGroupItem id="acts-FULL" value="FULL" />
          Full authority to act on my behalf
        </Label>
        <Label htmlFor="acts-REST" className="flex items-center gap-2 font-normal">
          <RadioGroupItem id="acts-REST" value="REST" />
          Authority with the following restrictions
        </Label>
      </RadioGroup>
      {field.value === "REST" && (
        <div className="grid gap-2">
          <Label htmlFor="restrictions">Restrictions</Label>
          <FormikTextarea id="restrictions" name="restrictions" rows={3} />
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form WV-2848 (West Virginia Power of Attorney) — prefilled from the
 * client + its practitioners. WV prints one representative (extras spill onto a
 * continuation sheet), five tax-matter rows, an expiration choice, and either
 * full or restricted authority.
 */
export function FormWv2848Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Representative (Agent)</SectionHeading>
          <PoaRepresentativesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Matters</SectionHeading>
          <WvMattersTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Expiration</SectionHeading>
          <ExpirationField />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Acts Authorized</SectionHeading>
          <ActsAuthorizedField />
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
